/**
 * The typed API client. Every convention from `fig/Fig-API-Reference.md` is encoded once here
 * rather than remembered per screen.
 */

import { FigApiError, NoRecordingError, parseProblem } from './errors.js';
import type { MyInvites } from './invites.js';
import { buildQuery, Pager, type ConversationFilters, type Sort } from './query.js';
import type {
  Agency, Agent, AssignableUser, Branding, Client, ConversationListItem, ConversationDetail,
  ConnectionSyncState, CreatedConnection,
  ProviderConnection, AgencyUser, ClientPortalUser, Page, PortalTermsState, SyncSchedule,
  Transcript, UserRole,
  DataExport, DeletionPreview, DeletionSchedule,
  CreateShareRequest, CreatedShare, SharedLink,
  SubmitFeedbackRequest, FeedbackItem,
} from './types.js';
import type { FigSession } from '../auth/session.js';

/** Playable-media store. Web hands back an object URL; React Native a file:// path. */
/**
 * The least a caller must know about a conversation before its audio can be cached correctly.
 *
 * Deliberately a structural type rather than `ConversationDetail`: a list row carries all three
 * fields too, so a caller is never forced to fetch the detail just to play something.
 */
export interface AudioIdentity {
  readonly id: string;
  readonly durationSeconds: number | null;
  /**
   * The waveform, when it has been extracted. **This is doing real work here, not decoration:**
   * ~100 amplitude peaks measured from the audio itself are a fingerprint of the bytes, and they
   * are already on the payload, so using them costs nothing — no extra request, no backend
   * change, and it still works with no network at all.
   */
  readonly waveformPeaks?: readonly number[] | null;
}

/**
 * A cache key that changes when the RECORDING changes, not when the conversation does.
 *
 * `duration` alone would catch most real cases; peaks make a collision essentially impossible,
 * because two different recordings would have to share both a duration and a hundred amplitude
 * measurements. When peaks have never been extracted the key falls back to duration, which is
 * strictly better than the id alone and never worse.
 *
 * Exported so it can be tested directly, and so anyone implementing another `BinaryStore` can see
 * what the key means rather than inferring it.
 */
export function audioCacheKey(c: AudioIdentity): string {
  let h = 2166136261 >>> 0;                       // FNV-1a, 32-bit
  const feed = (s: string) => {
    for (let i = 0; i < s.length; i += 1) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
  };
  feed(String(c.durationSeconds ?? ''));
  if (c.waveformPeaks && c.waveformPeaks.length > 0) feed(c.waveformPeaks.join(','));
  return `${c.id}-${h.toString(36)}`;
}

export interface BinaryStore {
  get(key: string): Promise<string | null>;
  put(key: string, data: ArrayBuffer, contentType: string): Promise<string>;
}

/**
 * What the API answers for a recording whose blob is encrypted.
 *
 * Recordings sealed from 2026-09-26 are AES-256-GCM in blob storage, so the bytes at `url` are
 * ciphertext. An UNSEALED recording still gets a 302 to the blob and never produces this shape —
 * 224 of those exist and they are not going to be re-encrypted before a client that can decrypt
 * has shipped.
 */
export interface SealedRecordingRef {
  /** Short-lived SAS URL for the CIPHERTEXT. */
  readonly url: string;
  readonly expiresInSeconds: number;
  /** `AES-256-GCM/FIG1` today. Checked, not assumed — a format we do not know is an error. */
  readonly cipher: string;
  /** Base64 AES-256 data key, unwrapped server-side from Key Vault for this one request. */
  readonly key: string;
}

/**
 * Turns a sealed blob into playable audio.
 *
 * **Why this is injected rather than implemented here.** The core has no runtime dependencies, on
 * purpose: it is vendored into two apps, so anything it imports has to be installed twice and kept
 * in step — and the vendoring drift lesson says that cost gets paid silently. AES-GCM is also
 * exactly the wrong thing to hand-roll. So the core defines the seam and each app supplies the
 * platform's own primitive:
 *
 * - **Browser** (`fig-client-portal`, the shared-call page): `crypto.subtle.decrypt` — free, native,
 *   no dependency at all.
 * - **React Native** (`fig-mobile`): Hermes has no `crypto.subtle`, so one pure-JS library. NOT a
 *   native crypto module — that is the dependency class that cost three TestFlight builds in August.
 *
 * `sealed` is the whole blob including its `FIG1` header; the implementation parses it. Throwing is
 * correct on a bad tag: it means the bytes are not what the key says they are.
 */
export type RecordingDecryptor =
  (sealed: ArrayBuffer, keyBase64: string, cipher: string) => Promise<ArrayBuffer>;

/** Raised when a sealed recording arrives and nothing was wired up to decrypt it. */
export class NoDecryptorError extends Error {
  // An explicit field, NOT a constructor parameter property. The portal enables
  // `erasableSyntaxOnly`, which forbids parameter properties because they emit real code rather
  // than erasing to nothing - and that flag is only visible through the portal's own npm script,
  // not through a bare `tsc --noEmit`.
  readonly conversationId: string;

  constructor(conversationId: string) {
    super(
      'This recording is encrypted and this app was built without a decryptor. ' +
      'Pass `recordingDecryptor` when constructing FigClient.',
    );
    this.conversationId = conversationId;
    this.name = 'NoDecryptorError';
  }
}

/** Raised when the server seals with a format this build does not know how to open. */
export class UnknownCipherError extends Error {
  readonly cipher: string;

  constructor(cipher: string) {
    super(`This recording uses "${cipher}", which this version of the app cannot open. Update the app.`);
    this.cipher = cipher;
    this.name = 'UnknownCipherError';
  }
}

export interface FigClientOptions {
  readonly baseUrl: string;
  readonly session: FigSession;
  readonly fetchImpl?: typeof fetch;
  /**
   * Where audio bytes live once fetched. Without it every replay re-downloads several
   * megabytes (finding S1) and a call longer than the SAS lifetime stops mid-playback (S2).
   * Keyed by CONVERSATION ID, never by URL: the URL rotates every 15 minutes, the bytes never
   * change. Recordings are immutable once synced, which makes this trivially safe.
   */
  readonly audioStore?: BinaryStore;

  /**
   * How to open a sealed recording. See {@link RecordingDecryptor}.
   *
   * Optional because a build without one still plays every recording stored before 2026-09-26 —
   * those are plaintext and arrive by redirect. It stops being optional the moment the backfill
   * runs, and a sealed recording reaching a client without one raises {@link NoDecryptorError}
   * rather than caching ciphertext and letting the player present it as a corrupt file.
   */
  readonly recordingDecryptor?: RecordingDecryptor;

  /**
   * How to fetch a recording's bytes from its storage URL, when the platform has a better way than
   * `fetch(...).arrayBuffer()`.
   *
   * <b>Why (2026-10-20).</b> On React Native `arrayBuffer()` is not a copy: the native side hands the
   * body over as a base64 data URL and it is decoded in JavaScript, on the JS thread, in one go -
   * ~230 ms for a 3 MB recording measured without a JIT, which is a frozen screen. The mobile app
   * passes a native download-to-disk-then-read instead. Omitted, the plain fetch is used (the web
   * has no such problem). Called with a bare storage URL: never send Fig's token with it.
   */
  readonly binaryFetch?: (url: string) => Promise<ArrayBuffer>;

  /**
   * Called after every request with what it cost, measured from the CLIENT's side.
   *
   * **Why the core owns this rather than each app.** The server already measures request-in to
   * response-out; what nobody measured was the half the person actually experiences — DNS, TLS,
   * the mobile network, cold start, parsing. Putting the timer in the one method every call passes
   * through means no call site has to remember, no screen can be missed, and **the web portal gets
   * it at the same time as the app** rather than being instrumented separately and later.
   *
   * The core stays dependency-free: it reports, and whoever constructs the client decides where
   * that goes. See `Telemetry-Implementation-Plan.md`.
   */
  readonly onRequest?: (info: RequestTiming) => void;
}

/**
 * What one request cost, and enough context to know whose fault it was.
 *
 * **`route` is a TEMPLATE, never the populated path.** `/api/conversations/{id}` — putting a real
 * id in here would create a new metric series per conversation and make the data useless within a
 * day. It also keeps identifiers out of telemetry, which is the standing privacy rule.
 */
export interface RequestTiming {
  /** The path template, already stripped of ids and of the query string entirely. */
  readonly route: string;
  readonly method: string;
  /** Client-measured: issued to parsed. 0 status means it never reached us. */
  readonly durationMs: number;
  readonly status: number;
  readonly ok: boolean;
  /** True when this was the first request since the client was constructed or resumed. */
  readonly cold: boolean;
  /** 1 on the first try; 2 after the single 401 re-exchange. Retries hide latency otherwise. */
  readonly attempt: number;
}

interface RequestInitLite {
  method?: string;
  query?: URLSearchParams;
  body?: unknown;
  signal?: AbortSignal;
  /** Set false only for the audio path, which handles its own redirect. */
  json?: boolean;
}

/**
 * Collapse a populated path back to its template.
 *
 * `/api/conversations/9f3c…/transcript` → `/api/conversations/{id}/transcript`. Without this,
 * every conversation opened creates its own metric series and the data is unreadable within a day
 * — and it would put identifiers into telemetry, which the privacy rule forbids.
 *
 * **Matches GUIDs and nothing else, which is exact rather than a heuristic.** Every route parameter
 * on the API is declared `{id:guid}` — the server will not route anything else — so "is a GUID" and
 * "is an id" are the same question here, and every non-GUID segment is a literal route word.
 *
 * My first version also treated any segment of 16+ characters as an id. That was wrong, and
 * `client-portal-users` (19 characters) is why: `/api/client-portal-users/{id}` would have
 * collapsed to `/api/{id}/{id}`, silently merging unrelated routes into one unreadable metric.
 * A heuristic was never needed — the contract was already exact.
 */
function routeTemplate(path: string): string {
  return path
    .split('/')
    .map((seg) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) ? '{id}' : seg)
    .join('/');
}

/**
 * Hex for a trace id. `crypto.getRandomValues` where it exists (browsers, and Hermes via
 * `expo-crypto`'s polyfill), falling back to Math.random — these ids only have to be unique
 * enough to join two records, they are not a security boundary.
 */
function randomHex(chars: number): string {
  const bytes = chars / 2;
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.getRandomValues) {
    const buf = new Uint8Array(bytes);
    c.getRandomValues(buf);
    return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  let out = '';
  for (let i = 0; i < bytes; i += 1) {
    out += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
  }
  return out;
}

export class FigClient {
  readonly #baseUrl: string;
  readonly #session: FigSession;
  readonly #fetch: typeof fetch;
  readonly #audio: BinaryStore | undefined;
  readonly #onRequest: ((info: RequestTiming) => void) | undefined;
  readonly #decrypt: RecordingDecryptor | undefined;
  readonly #binaryFetch: ((url: string) => Promise<ArrayBuffer>) | undefined;
  /** Flipped false after the first completed request; see `cold` on RequestTiming. */
  #cold = true;

  constructor(opts: FigClientOptions) {
    this.#baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.#session = opts.session;
    this.#fetch = opts.fetchImpl ?? globalThis.fetch;
    this.#audio = opts.audioStore;
    this.#decrypt = opts.recordingDecryptor;
    this.#binaryFetch = opts.binaryFetch;
    this.#onRequest = opts.onRequest;
  }

  // -------------------------------------------------------------- transport

  async #raw(path: string, init: RequestInitLite, retry = true): Promise<Response> {
    const token = await this.#session.getToken();
    const qsText = init.query ? String(init.query) : "";
    const qs = qsText.length > 0 ? `?${qsText}` : "";

    /*
     * One trace, phone to database.
     *
     * A W3C `traceparent` makes the server's span a child of this request instead of an unrelated
     * record, so "this tap was slow" and "this query was slow" become the same trace rather than
     * two things somebody has to correlate by eye and timestamp. The API already runs
     * OpenTelemetry and adopts an incoming parent without any further work.
     */
    const traceId = randomHex(32);
    const spanId = randomHex(16);

    const started = Date.now();
    const attempt = retry ? 1 : 2;
    let status = 0;
    let res: Response;
    try {
      res = await this.#fetch(`${this.#baseUrl}${path}${qs}`, {
        method: init.method ?? 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          // 01 = sampled. We are not sampling anything out at this volume, and a launch with
          // partial traces is a launch where the interesting request is the missing one.
          traceparent: `00-${traceId}-${spanId}-01`,
          ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
        ...(init.signal ? { signal: init.signal } : {}),
      });
      status = res.status;
    } finally {
      // Reported even when fetch THREW — a request that never reached the server is the most
      // important latency there is, and a `finally` is the only place that sees it. Status 0
      // says "never arrived", which is a different problem from any HTTP code.
      const cold = this.#cold;
      this.#cold = false;
      try {
        this.#onRequest?.({
          route: routeTemplate(path),
          method: init.method ?? 'GET',
          durationMs: Date.now() - started,
          status,
          ok: status >= 200 && status < 300,
          cold,
          attempt,
        });
      } catch { /* telemetry must never break the call it is measuring */ }
    }

    // Re-exchange ONCE on a 401 and retry. Not a loop: if a fresh token is still refused, the
    // problem is the account, not the token, and retrying forever would hide that.
    if (res.status === 401 && retry) {
      await this.#session.forceRefresh();
      return this.#raw(path, init, false);
    }
    return res;
  }

  async #request<T>(path: string, init: RequestInitLite = {}): Promise<T> {
    const res = await this.#raw(path, init);
    if (!res.ok) throw await this.#toError(res);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  async #toError(res: Response): Promise<FigApiError> {
    const text = await res.text().catch(() => '');
    return new FigApiError(res.status, parseProblem(text), text || null);
  }

  // -------------------------------------------------------------- agency

  /** Agency roles only. A portal token gets 403 - portals want `getBranding()`. */
  /**
   * Tell the server this visit is still going.
   *
   * **Fire-and-forget, and it must stay that way.** Recording that somebody used Fig is the least
   * important thing happening on any screen; if it fails, nothing the person was doing should
   * notice. Every call site awaits nothing and every failure is swallowed here.
   *
   * The server supplies WHO from the token — this sends only an opaque key, a coarse event name
   * and a route TEMPLATE. See the API's UsageController for why the screen name is an allow-list
   * rather than a path.
   */
  /**
   * Playback started (2026-10-20): once per playback, replays from the cache included.
   * Fire-and-forget like recordUsage: a failed log must never interrupt listening.
   */
  /**
   * Time actually heard since the last report (2026-10-21). Fire-and-forget; a report sums these.
   */
  recordListened(conversationId: string, seconds: number): void {
    const s = Math.round(seconds);
    if (s < 1) return;
    void this.#request(`/api/conversations/${conversationId}/listened`, { method: 'POST', body: { seconds: s } })
      .catch(() => { /* deliberately silent */ });
  }

  /**
   * Resolves either way and never rejects (reconciled 2026-10-21): the app fires and forgets, the
   * portal chains `.catch` - both are right against a promise that cannot fail.
   */
  recordPlayed(conversationId: string): Promise<void> {
    return this.#request(`/api/conversations/${conversationId}/played`, { method: 'POST' })
      .then(() => undefined, () => undefined);
  }

  /**
   * A one-time, sixty-second ticket that signs the browser in as this person (2026-10-20), so
   * "Open the portal" does not ask them to sign in again. Agency admins only; 503 where the
   * identity provider is not configured. Callers fall back to the plain address on any failure.
   */
  createWebHandoff(): Promise<{ ticket: string; expiresInSeconds: number }> {
    return this.#request('/api/auth/web-handoff', { method: 'POST' });
  }

  recordUsage(body: {
    sessionKey: string;
    event?: 'open' | 'foreground' | 'screen' | 'search' | 'play';
    screen?: string;
    platform?: string;
    appVersion?: string;
  }): void {
    void this.#request('/api/usage/heartbeat', { method: 'POST', body }).catch(() => {
      // Deliberately silent. A usage ping that surfaced an error would be a analytics feature
      // interrupting the product it is measuring.
    });
  }

  getAgency(): Promise<Agency> { return this.#request('/api/agency'); }

  /**
   * The white-label surface, readable by ANY authenticated caller. This is what a portal
   * renders. `/api/agency` leaked agency internals to end-clients once; do not reach for it
   * from portal code.
   */
  getBranding(): Promise<Branding> { return this.#request('/api/agency/branding'); }

  updateAgency(patch: {
    timeZoneId?: string; brandingDisplayName?: string; brandingAccentColor?: string;
  }): Promise<Agency> {
    return this.#request('/api/agency', { method: 'PATCH', body: patch });
  }

  // -------------------------------------------------------------- clients

  listClients(sort?: Sort<'Name' | 'CreatedAt'>): Promise<readonly Client[]> {
    return this.#request('/api/clients', { query: buildQuery({}, sort) });
  }
  /** 404 when out of scope, never 403 - "it exists but you may not see it" is a disclosure. */
  getClient(id: string): Promise<Client> { return this.#request(`/api/clients/${id}`); }
  createClient(name: string): Promise<Client> {
    return this.#request('/api/clients', { method: 'POST', body: { name } });
  }
  updateClient(id: string, patch: { name?: string; portalAccessDisabled?: boolean }): Promise<Client> {
    return this.#request(`/api/clients/${id}`, { method: 'PATCH', body: patch });
  }

  // -------------------------------------------------------------- connections and agents

  listConnections(sort?: Sort<'ConnectionName' | 'LastSyncedAt' | 'SyncStatus'>): Promise<readonly ProviderConnection[]> {
    return this.#request('/api/connections', { query: buildQuery({}, sort) });
  }
  getConnection(id: string): Promise<ProviderConnection> { return this.#request(`/api/connections/${id}`); }
  listConnectionAgents(connectionId: string): Promise<readonly Agent[]> {
    return this.#request(`/api/connections/${connectionId}/agents`);
  }
  /**
   * Creates a connection. **The API verifies the key with the provider before storing it**
   * (since 2026-09-07), so a rejection here is a real answer rather than a delayed one:
   *
   *   400 - the provider refused the key. Show the message; it is the customer's to fix.
   *   502 - the provider could not be reached. Nothing was saved. NOT the customer's fault,
   *         and telling them their key is wrong would send them to reissue a good credential.
   *
   * The response carries the agents the key actually reached, which is the only honest way for
   * a client to say what a first sync will pull. Before this, the onboarding flow's "verifying"
   * step had nothing behind it and any stated volume was invented.
   */
  createConnection(body: {
    clientId: string; connectionName: string; apiKey: string; syncSchedule: SyncSchedule;
  }): Promise<CreatedConnection> {
    return this.#request('/api/connections', { method: 'POST', body });
  }

  /**
   * Sync this connection now, on top of the schedule rather than instead of it.
   *
   * The sync job's own trigger is Function-key protected and unreachable from a client, so this
   * is the door the API opens instead. Without it, pull-to-refresh can only re-run the same
   * query and hand back the same rows - which is not what the gesture promises.
   *
   * Since 2026-10-20 the server answers at once - the sync runs in the background - with a 202
   * saying which: `queued` (started), `running` (one is already going: show its progress) or
   * `recent` (one finished seconds ago: nothing to wait for). An older server answered 409 for
   * "already running"; that still maps to the same thing, because it is not a failure.
   */
  async syncNow(connectionId: string): Promise<'started' | 'already-running' | 'recent'> {
    try {
      const body = await this.#request<{ state?: string } | undefined>(
        `/api/connections/${connectionId}/sync`, { method: 'POST' });
      if (body?.state === 'running') return 'already-running';
      if (body?.state === 'recent') return 'recent';
      return 'started';
    } catch (e) {
      if (e instanceof FigApiError && e.isConflict) return 'already-running';
      throw e;
    }
  }

  /** Is this connection syncing, and how far has it got? Cheap; polled while a sync runs. */
  getSyncState(connectionId: string): Promise<ConnectionSyncState> {
    return this.#request(`/api/connections/${connectionId}/sync`);
  }
  /** Omit `apiKey` to leave it untouched. It is write-only and never returned, not even masked. */
  updateConnection(id: string, patch: {
    connectionName?: string; syncSchedule?: SyncSchedule; apiKey?: string;
  }): Promise<ProviderConnection> {
    return this.#request(`/api/connections/${id}`, { method: 'PATCH', body: patch });
  }

  /**
   * Agency-wide agents. Approved to be built; 404 until it ships.
   *
   * `listAllAgents()` below falls back to the per-connection fan-out so a screen can be built
   * now - but it is a fallback with an N+1 in it, not the design. When the endpoint lands this
   * method starts working and the fallback stops being reached, with no screen changing.
   */
  listAgents(): Promise<readonly Agent[]> { return this.#request('/api/agents'); }

  async listAllAgents(): Promise<readonly Agent[]> {
    try {
      return await this.listAgents();
    } catch (e) {
      if (!(e instanceof FigApiError) || !e.isNotFound) throw e;
      const connections = await this.listConnections();
      const pages = await Promise.all(connections.map((c) => this.listConnectionAgents(c.id)));
      const seen = new Map<string, Agent>();
      for (const page of pages) for (const a of page) seen.set(a.id, a);
      return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  // -------------------------------------------------------------- conversations

  async listConversations(
    filters: ConversationFilters,
    pager: Pager<'StartedAt' | 'Duration' | 'Sentiment' | 'AssignedAt'>,
    signal?: AbortSignal,
  ): Promise<Page<ConversationListItem>> {
    const query = buildQuery(filters, pager.sort, pager);
    const page = await this.#request<Page<ConversationListItem>>('/api/conversations', {
      query, ...(signal ? { signal } : {}),
    });
    pager.accept(page.nextCursor);
    return page;
  }

  /** Follow `nextCursor` to the end. The honest way to "get them all" - see Pager's note. */
  async collectAllConversations(
    filters: ConversationFilters,
    sort: Sort<'StartedAt' | 'Duration' | 'Sentiment' | 'AssignedAt'>,
    maxPages = 200,
  ): Promise<readonly ConversationListItem[]> {
    const pager = new Pager(sort, 100);
    const all: ConversationListItem[] = [];
    for (let i = 0; i < maxPages; i++) {
      const page = await this.listConversations(filters, pager);
      all.push(...page.items);
      if (pager.exhausted) break;
    }
    return all;
  }

  /**
   * The sources this caller's calls actually arrived through — `['phone', 'web']`.
   *
   * For building a source filter from what exists rather than from a hardcoded list. `channel` is
   * free text written by the provider, so a fixed list in the UI acquires a silent expiry date the
   * moment a provider adds a value. Lowercased and deduplicated server-side.
   */
  listChannels(): Promise<readonly string[]> { return this.#request('/api/conversations/channels'); }

  getConversation(id: string): Promise<ConversationDetail> { return this.#request(`/api/conversations/${id}`); }
  /**
   * Returns `{ conversationId, turns }` - an object, not a bare array.
   *
   * TURNS WITH NO TEXT ARE DROPPED HERE, and the reason is measured rather than theoretical.
   * In the 214 real conversations on the dev branch, **264 of 1,538 turns (17.2%) have null or
   * empty text, across 81 conversations - 38% of the corpus.** The provider emits them around
   * tool calls and transfers.
   *
   * Both clients rendered `turns.map(...)` with no filter, so more than a third of real calls
   * displayed empty bubbles: on mobile a tappable, labelled, entirely blank card that seeks the
   * player to a moment where nobody says anything; in the portal an "Assistant" label with
   * nothing after it. It reads as a broken transcript, which is worse than a shorter one.
   *
   * Filtering here rather than at the two render sites is deliberate: "a turn with no words is
   * not a turn" is a fact about the data, not a presentation preference, and a fix duplicated in
   * two repos is a fix that drifts. Doing it before the emptiness check also means a transcript
   * that is ENTIRELY blank turns correctly shows the "no transcript" state instead of an empty
   * list - which a render-site filter would have got wrong.
   */
  async getTranscript(id: string): Promise<Transcript> {
    const t = await this.#request<Transcript>(`/api/conversations/${id}/transcript`);
    return { ...t, turns: t.turns.filter((turn) => turn.text?.trim()) };
  }

  /**
   * Detail and transcript together. Finding S4: fetching them sequentially makes the reader
   * wait twice for one screen.
   */
  async getConversationWithTranscript(id: string): Promise<{
    conversation: ConversationDetail; transcript: Transcript;
  }> {
    const [conversation, transcript] = await Promise.all([this.getConversation(id), this.getTranscript(id)]);
    return { conversation, transcript };
  }

  /**
   * Detail AND transcript for up to 25 conversations in ONE request — the prefetch path
   * (2026-10-20, `fig/Prefetch-Plan.md`).
   *
   * On a roaming or weak connection each request costs 1.2-3s regardless of how fast the server
   * is, so twenty calls fetched one by one is a minute of radio time; this is one request.
   *
   * The server records these as **Prefetched, not Viewed** — the data reached the device, nobody
   * necessarily read it — so the access log stays honest. Opening a call still calls
   * {@link getConversationWithTranscript}, which records the view.
   *
   * Ids the caller cannot see are simply absent. Throws a FigApiError with status 404 against a
   * server that predates the endpoint; the caller treats that as "no prefetch", never as a reason
   * to fall back to the single reads (which would log views that never happened).
   */
  async getConversationsBatch(ids: readonly string[]): Promise<readonly {
    conversation: ConversationDetail; transcript: Transcript;
  }[]> {
    if (ids.length === 0) return [];
    const res = await this.#request<{ items: { conversation: ConversationDetail; transcript: Transcript }[] }>(
      `/api/conversations/batch?ids=${ids.map(encodeURIComponent).join(',')}`);
    // The same rule as getTranscript: a turn with no words is not a turn.
    return res.items.map((i) => ({
      conversation: i.conversation,
      transcript: { ...i.transcript, turns: i.transcript.turns.filter((turn) => turn.text?.trim()) },
    }));
  }

  /**
   * A playable URL for a recording, cached by **what the recording IS**, not by which
   * conversation it belongs to.
   *
   * The endpoint 302s to a 15-minute Blob SAS and the bytes never pass through the API. Two
   * consequences the design has to respect and this method handles:
   *   - the SAS expires, so a 40-minute call would stop mid-playback if the player streamed
   *     from it. Fetching the bytes once and playing from the store removes that entirely.
   *   - replaying re-downloads megabytes over mobile data unless something caches them.
   *
   * ── WHY THIS TAKES A CONVERSATION AND NOT AN ID ─────────────────────────────────────────────
   *
   * It used to take `conversationId` and cache under it, on a stated assumption: *"recordings are
   * immutable once synced, which is what makes caching trivially safe here."* **That assumption is
   * false, and I wrote it.** Rasel hit the consequence on 2026-09-16: the demo tenant was reseeded
   * with entirely new recordings under the same stable ids, and his phone kept playing the old
   * audio under the new transcripts — a voice he had specifically asked us to remove, still
   * talking. Clearing the cache fixed his copy; nothing fixed the defect.
   *
   * And it was never only a demo problem. A provider that re-processes a recording, a correction,
   * a re-sync — any of those produce new bytes at the same conversation id, and the cache would
   * have served the old ones **forever**, with no expiry and no way for the app to notice.
   *
   * Taking the conversation rather than an id makes the mistake unconstructible: there is no
   * signature here that lets a caller supply an id without the fields that say which version of
   * the audio it means. That is the 2026-09-06 rule applied — *if a code path is wrong without a
   * setup step, the step belongs in a type the path cannot compile without, not in prose beside
   * it.*
   *
   * Throws `NoRecordingError` for a text-only conversation. That is a NORMAL state - render
   * "transcript only", never an error.
   */
  async getAudioUrl(
    conversation: AudioIdentity,
    opts: { prefetch?: boolean } = {},
  ): Promise<{ url: string; fromCache: boolean }> {
    const conversationId = conversation.id;
    const key = audioCacheKey(conversation);

    if (this.#audio) {
      const hit = await this.#audio.get(key);
      if (hit) return { url: hit, fromCache: true };
    }
    // `prefetch` only changes what the server LOGS (AudioPrefetched rather than Audio): a link issued
    // so the app can fetch ahead of a tap is not somebody pressing play.
    const res = await this.#raw(
      `/api/conversations/${conversationId}/audio${opts.prefetch ? '?prefetch=true' : ''}`, {});
    if (res.status === 404) throw new NoRecordingError(conversationId);
    if (!res.ok) throw await this.#toError(res);

    // TWO RESPONSE SHAPES, and the fork is decided by what came back rather than by a flag.
    //
    // An UNSEALED recording answers 302 and `fetch` has already followed it, so `res` holds audio
    // bytes and `res.url` is the blob - exactly as it has always worked. A SEALED one answers 200
    // with JSON, because the bytes at the URL are ciphertext and a redirect would hand the player
    // noise. Reading the content type means neither this client nor the server has to be told
    // which era the other is in, which matters while 224 plaintext recordings still exist.
    const contentType = res.headers.get('content-type') ?? '';
    const plainBytes = contentType.includes('application/json')
      ? await this.#openSealed(await res.json() as SealedRecordingRef, conversationId)
      : null;

    if (plainBytes) {
      // Deliberately cached DECRYPTED. The store is per-device private storage, the key is
      // per-request and not stored anywhere, and caching ciphertext would mean re-fetching a key
      // on every replay - so a cache hit would still need the network, which is the one thing the
      // cache exists to avoid. Offline replay only works if what is cached is playable.
      if (!this.#audio) throw new Error('A sealed recording needs an audioStore to decrypt into.');
      return { url: await this.#audio.put(key, plainBytes, 'audio/mpeg'), fromCache: false };
    }

    if (!this.#audio) return { url: res.url, fromCache: false };
    const buf = await res.arrayBuffer();
    const type = contentType || 'audio/mpeg';
    return { url: await this.#audio.put(key, buf, type), fromCache: false };
  }

  /**
   * Fetches the ciphertext and decrypts it.
   *
   * The ciphertext is fetched with a BARE fetch, not `#raw`: the SAS URL is a storage URL and
   * attaching Fig's Authorization header to it would send a session token to a host that is not
   * ours. That is the sort of thing that only ever shows up in somebody else's access log.
   */
  async #openSealed(ref: SealedRecordingRef, conversationId: string): Promise<ArrayBuffer> {
    if (!this.#decrypt) throw new NoDecryptorError(conversationId);

    // Checked rather than passed through. A format this build does not know is a clear "update the
    // app", not a decryption failure somewhere further down that reads like corrupt audio.
    if (ref.cipher !== 'AES-256-GCM/FIG1') throw new UnknownCipherError(ref.cipher);

    if (this.#binaryFetch) return this.#decrypt(await this.#binaryFetch(ref.url), ref.key, ref.cipher);

    const blob = await this.#fetch(ref.url);
    if (!blob.ok) throw new Error(`The recording could not be fetched (${blob.status}).`);

    return this.#decrypt(await blob.arrayBuffer(), ref.key, ref.cipher);
  }

  /** Returns 200 with the FULL updated conversation - not 201, not an empty body. */
  assign(conversationId: string, assignedUserId: string, note?: string): Promise<ConversationDetail> {
    return this.#request(`/api/conversations/${conversationId}/assignment`, {
      method: 'POST', body: note === undefined ? { assignedUserId } : { assignedUserId, note },
    });
  }
  /** Also 200 with the full conversation, not 204. */
  unassign(conversationId: string): Promise<ConversationDetail> {
    return this.#request(`/api/conversations/${conversationId}/assignment`, { method: 'DELETE' });
  }

  /** Open to every agency role - id and name only. `GET /api/users` is Admin-only. */
  listAssignableUsers(): Promise<readonly AssignableUser[]> { return this.#request('/api/users/assignable'); }

  // -------------------------------------------------------------- administration (Admin only)

  listUsers(sort?: Sort<'CreatedAt' | 'Name' | 'Email' | 'Role'>): Promise<readonly AgencyUser[]> {
    return this.#request('/api/users', { query: buildQuery({}, sort) });
  }
  inviteUser(body: { email: string; name: string; role: UserRole }): Promise<AgencyUser> {
    return this.#request('/api/users/invite', { method: 'POST', body });
  }
  setUserDisabled(id: string, disabled: boolean): Promise<AgencyUser> {
    return this.#request(`/api/users/${id}`, { method: 'PATCH', body: { disabled } });
  }
  /** Revokes a PENDING invitation. 409 once accepted - disable them instead. */
  revokeInvitation(id: string): Promise<void> { return this.#request(`/api/users/${id}`, { method: 'DELETE' }); }

  listPortalUsers(clientId?: string): Promise<readonly ClientPortalUser[]> {
    const q = new URLSearchParams();
    if (clientId) q.set('clientId', clientId);
    return this.#request('/api/client-portal-users', { query: q });
  }
  invitePortalUser(body: { clientId: string; email: string; name: string }): Promise<ClientPortalUser> {
    return this.#request('/api/client-portal-users/invite', { method: 'POST', body });
  }
  setPortalUserDisabled(id: string, disabled: boolean): Promise<ClientPortalUser> {
    return this.#request(`/api/client-portal-users/${id}`, { method: 'PATCH', body: { disabled } });
  }

  // -------------------------------------------------------------- account deletion

  /**
   * What deleting this account would destroy, read at the moment of asking.
   *
   * Deliberately not cached and not folded into the session: the confirmation screen's whole job
   * is to make a consequence concrete, and a stale count is a promise about somebody's data that
   * nobody has checked.
   */
  previewAccountDeletion(): Promise<DeletionPreview> {
    return this.#request('/api/account/deletion-preview');
  }

  /**
   * Schedules the deletion and returns the date it completes.
   *
   * Throws `FigApiError` with status 409 when the caller is the last admin of an agency that
   * still has other people in it - the refusal carries `error: 'lastAdmin'`.
   */
  scheduleAccountDeletion(): Promise<DeletionSchedule> {
    return this.#request('/api/account/deletion', { method: 'POST' });
  }

  /** The pending schedule, or null when there is none. 404 is the ordinary answer here. */
  async getAccountDeletion(): Promise<DeletionSchedule | null> {
    try {
      return await this.#request<DeletionSchedule>('/api/account/deletion');
    } catch (e) {
      if (e instanceof FigApiError && e.status === 404) return null;
      throw e;
    }
  }

  /**
   * Changed their mind. **This call is what justifies keeping the data during the window at
   * all** - a recovery period nobody can use is retention for a purpose that does not exist.
   */
  cancelAccountDeletion(): Promise<void> {
    return this.#request('/api/account/deletion', { method: 'DELETE' });
  }

  /**
   * Ask for everything Fig holds about this account.
   *
   * **Idempotent per tenant**: asking again while one is already building returns the one in
   * flight rather than starting a second. Tapping twice is not a mistake somebody should be told
   * off for, and the server is the right place to enforce that rather than the screen.
   */
  /**
   * Makes a call reachable by a URL and returns the link.
   *
   * The link and code can be seen again from `listSharedLinks()` while the link is live (since
   * 2026-09-25) - they are kept encrypted for the sharer, and the public page still only ever
   * looks a link up by its hash.
   */
  /**
   * Send feedback. **One required field, on purpose** - see SubmitFeedbackRequest.
   *
   * There is no optimistic UI behind this and there should not be. Telling somebody their report
   * was sent when the request is still in flight means a failure shows up as a report that
   * silently never existed, which is the one outcome that teaches people not to bother.
   */
  submitFeedback(request: SubmitFeedbackRequest): Promise<{ id: string; submittedAt: string }> {
    return this.#request('/api/feedback', { method: 'POST', body: request });
  }

  /**
   * What this person has reported, newest first, with any reply.
   *
   * **Scoped to the caller by the server, not to the agency.** Feedback often quotes a call or
   * criticises a colleague's work, and an admin reading their staff's bug reports is surveillance
   * nobody asked for.
   */
  async listMyFeedback(): Promise<readonly FeedbackItem[]> {
    return await this.#request<readonly FeedbackItem[]>('/api/feedback');
  }

  /**
   * Called once the reply has actually been on screen.
   *
   * **Separate from reading the list, deliberately.** If fetching marked replies seen, a background
   * refresh nobody looked at would set "they know", and the only number that tells us whether this
   * loop closes would quietly become false.
   */
  markFeedbackReplySeen(id: string): Promise<void> {
    return this.#request(`/api/feedback/${id}/reply-seen`, { method: 'POST' });
  }

  /**
   * This person's invite codes, topped up to the current quota by the server. `enabled: false`
   * means the rollout is off or they do not hold codes - the screen should not exist then. An
   * older API without the rollout answers 404, which is read the same way.
   */
  async getMyInvites(): Promise<MyInvites> {
    try {
      return await this.#request<MyInvites>('/api/invites/mine');
    } catch (e) {
      if (e instanceof FigApiError && e.status === 404) return { enabled: false, quota: 0, invites: [] };
      throw e;
    }
  }

  createShare(request: CreateShareRequest): Promise<CreatedShare> {
    // `#raw` serialises `body` and sets Content-Type itself. Stringifying here as well sent the
    // API a JSON *string* instead of an object, and `headers` is not part of RequestInitLite,
    // which is what failed the portal's typecheck (and blocked its deploys) on 2026-09-23.
    return this.#request('/api/shared-links', { method: 'POST', body: request });
  }

  /** Everything this account is sharing, newest first - live, expired and revoked alike. */
  listSharedLinks(): Promise<readonly SharedLink[]> {
    return this.#request('/api/shared-links');
  }

  /** Live links for one call, for the indicator on the call screen. */
  listSharesForConversation(conversationId: string): Promise<readonly SharedLink[]> {
    return this.#request(`/api/shared-links/for-conversation/${encodeURIComponent(conversationId)}`);
  }

  /**
   * Turns a link off, permanently. Not a toggle: the reason somebody revoked a link does not
   * expire, and an un-revoke would mean a URL that a person was told was dead can come back to
   * life without them ever knowing.
   *
   * Idempotent - revoking an already-revoked link succeeds.
   */
  revokeShare(id: string): Promise<void> {
    return this.#request(`/api/shared-links/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  requestDataExport(): Promise<DataExport> {
    return this.#request('/api/account/export', { method: 'POST' });
  }

  /**
   * How the export is going, and the download link when there is one.
   *
   * **404 is the ordinary answer** for an account that has never asked - a different thing from
   * "it failed", and the screen renders a different state for each.
   */
  async getDataExport(): Promise<DataExport | null> {
    try {
      return await this.#request<DataExport>('/api/account/export');
    } catch (e) {
      if (e instanceof FigApiError && e.status === 404) return null;
      throw e;
    }
  }

  /**
   * Changes a colleague's role. Exists so the last-admin refusal above is not a dead end: the
   * way out is to make somebody else an admin first.
   */
  setUserRole(id: string, role: UserRole): Promise<AgencyUser> {
    return this.#request(`/api/users/${id}/role`, { method: 'PATCH', body: { role } });
  }

  // -------------------------------------------------------------- portal terms

  /** Read `acceptanceRequired`; do not compare versions yourself. */
  getPortalTerms(): Promise<PortalTermsState> { return this.#request('/api/portal/terms'); }
  /** Echo back the version you actually displayed. 409 if it is not current. */
  acceptPortalTerms(version: string): Promise<void> {
    return this.#request('/api/portal/terms', { method: 'POST', body: { version } });
  }
}
